import type { SupabaseClient } from '@supabase/supabase-js';
import { openModal, showToast } from '@vario-connect/frontend-design';
import { awaitJob } from '../../lib/await-job.js';
import { httpErrorText } from '../../lib/session-expiry.js';

/**
 * „Vario-Artikel zuordnen"-Dialog für ein Otto-Angebot, bewusst schlank gehalten:
 *  - KEIN „Artikel in Vario anlegen" (Angebote werden nur ZUGEORDNET, nicht importiert).
 *  - Aufheben als ZWEISTUFIGER Klick (kein window.confirm - bei uns verboten).
 *  - Vorschlag (gleiche SKU/EAN in Vario) als 1-Klick-Zuordnung, plus freie Suche.
 * Zuordnung ist frei: jedes Angebot kann jedem bestehenden Vario-Artikel zugewiesen werden.
 */
interface AssignDialogCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  unit: {
    sku: string;
    ean: string | null;
    title: string | null;
    /** Gesetzt = bereits zugeordnet → Aufheben-Zweig. */
    varioArticleId?: string | null;
    varioArticleNumber?: string | null;
    /** SKU/EAN-Match aus vario.article_match_suggestions (blauer Filter) → Vorschlag. */
    suggestedVarioArticleId?: string | null;
    suggestedVarioArticleNumber?: string | null;
    /** Aktueller Otto-Angebotspreis (Cent) — bei Zuordnung als Kanalpreis gesetzt, falls fehlt. */
    priceCents?: number | null;
  };
  /** Nach erfolgreicher Zuordnung/Aufhebung → Zeile neu laden. `category`/`brand` (nur nach
   *  Zuordnung): sollen OTTO-Kategorie bzw. OTTO-Marke übernommen werden (Einstellung bzw. Antwort
   *  im Dialog)? Die Bestellansicht braucht das, weil sie die Kanal-Anlage selbst macht. */
  onDone: (info?: { category: OfferCategoryChoice | null; brand: OfferBrandChoice | null }) => void;
  /** Nach der Zuordnung den Otto-Verkaufskanal am Vario-Artikel anlegen + Lister-Zeile
   *  (otto-order-provision-channel). Vorgabe true; die Bestellansicht setzt false, weil sie das
   *  in ihrem onDone selbst mit dem Bestellpreis erledigt. */
  provisionChannel?: boolean;
}

/** Kategorie des Angebots bei OTTO, aufgelöst auf Kategoriegruppe (otto_get_offer_category). */
export interface OfferCategoryChoice {
  group: string;
  leaf: string;
}

export type CategoryMode = 'take' | 'skip' | 'ask';

/** Marke des Angebots bei OTTO (otto_get_offer_brand). */
export interface OfferBrandChoice {
  id: string;
  name: string;
}

/** Ergebnis von applyOfferBrand. `kept`: die Lister-Zeile hatte schon eine Marke. `brandAdded`:
 *  die Marke war noch nicht unter den präferierten Marken und wurde aufgenommen. */
export type ApplyBrandResult =
  | { status: 'skipped' }
  | { status: 'kept' }
  | { status: 'set'; brandAdded: boolean }
  | { status: 'error'; message: string };

/**
 * Setzt nach der Kanal-Anlage die OTTO-Marke des Angebots an der Lister-Zeile und nimmt sie in die
 * präferierten Marken auf (otto_assign_offer_brand, eine Transaktion). Eine schon gesetzte Marke
 * bleibt unverändert. Anders als die Kategorie lebt die Marke nur in der App, VARIO bleibt unberührt.
 */
export async function applyOfferBrand(
  supabase: SupabaseClient,
  provision: ProvisionResult,
  choice: OfferBrandChoice,
): Promise<ApplyBrandResult> {
  const listerRowId = provision.listerRow?.listing_id;
  if (!listerRowId) return { status: 'skipped' };
  try {
    const { data, error } = await supabase.rpc('otto_assign_offer_brand', { _listing_id: listerRowId, _brand_id: choice.id });
    if (error) return { status: 'error', message: `Marke nicht übernommen: ${error.message}` };
    const row = (Array.isArray(data) ? data[0] : data) as { status?: string; brand_added?: boolean } | null;
    if (row?.status === 'kept') return { status: 'kept' };
    return { status: 'set', brandAdded: row?.brand_added === true };
  } catch (e) {
    return { status: 'error', message: `Marke nicht übernommen: ${(e as Error).message}` };
  }
}

/** Antwort von otto-order-provision-channel, soweit Dialog und Bestellansicht sie auswerten. */
export interface ProvisionResult {
  error?: { message?: string };
  skipped?: string;
  articleId?: string;
  listingId?: string;
  hasVarioCategory?: boolean | null;
  eanWritten?: { ok?: boolean; message?: string } | null;
  listerRow?: { listing_id?: string; inserted?: boolean } | null;
}

/** Ergebnis von applyOfferCategory. `groupAdded`: die Kategoriegruppe war noch nicht ausgewählt
 *  und wurde samt Kategorie-Sync in VARIO übernommen. */
export type ApplyCategoryResult =
  | { status: 'skipped' }
  | { status: 'set'; groupAdded: boolean }
  | { status: 'error'; message: string };

type CategoryOpts = { supabase: SupabaseClient; supabaseUrl: string; token: string; onStatus?: (msg: string) => void };

/**
 * Stößt den Kategorie-Sync (otto.sync_categories) an und wartet auf sein Ende. Läuft gerade einer,
 * hat der die neue Gruppe evtl. nicht gesehen → abwarten und einmal neu anstoßen. Rückgabe:
 * Fehlertext oder null.
 */
async function syncCategoryTree(supabase: SupabaseClient): Promise<string | null> {
  for (let round = 0; round < 2; round += 1) {
    const { data, error } = await supabase.rpc('otto_enqueue_category_push');
    if (error) return `Kategorie-Sync nicht gestartet: ${error.message}`;
    const row = (Array.isArray(data) ? data[0] : data) as { job_id?: string; reason?: string | null } | null;
    if (!row?.job_id) return 'Kategorie-Sync nicht gestartet.';
    let job;
    try { job = await awaitJob(supabase, row.job_id, { timeoutMs: 180_000, pollIntervalMs: 2000 }); }
    catch (e) { return `Kategorie-Sync dauert zu lange: ${(e as Error).message}`; }
    if (job.status !== 'completed') return `Kategorie-Sync fehlgeschlagen: ${job.error ?? job.status}`;
    if (row.reason !== 'running') return null;
  }
  return null;
}

/**
 * Setzt nach der Kanal-Anlage die OTTO-Kategorie des Angebots, aber nur, wenn das Kanal-Listing
 * in VARIO noch keine Kategorie hat. Vorher wird die Kategoriegruppe sichergestellt: Ist sie nicht
 * ausgewählt, kommt sie in die Auswahl (otto_add_selected_category_group) und der Kategorie-Sync
 * übernimmt sie in den VARIO-Baum. Sonst stünde am Artikel eine Kategorie, deren Gruppe weder
 * ausgewählt noch in VARIO vorhanden ist.
 *
 * Danach derselbe Weg wie die Kategorie-Auswahl im Lister: otto-assign-listing-category
 * (VARIO-Kategorie aus dem OTTO-Baum am Listing) und otto_set_listing_category (Lister-Zeile).
 * Findet die Function die Kategorie nicht im Baum (Gruppe ausgewählt, aber nie synchronisiert),
 * einmal synchronisieren und erneut versuchen.
 */
export async function applyOfferCategory(
  opts: CategoryOpts,
  provision: ProvisionResult,
  choice: OfferCategoryChoice,
): Promise<ApplyCategoryResult> {
  if (provision.hasVarioCategory !== false) return { status: 'skipped' }; // schon gesetzt oder unbekannt
  const listerRowId = provision.listerRow?.listing_id;
  if (!provision.articleId || !provision.listingId || !listerRowId) return { status: 'skipped' };
  const fail = (message: string): ApplyCategoryResult => ({ status: 'error', message });
  try {
    const { data: added, error: addErr } = await opts.supabase.rpc('otto_add_selected_category_group', { _group: choice.group });
    if (addErr) return fail(`Kategoriegruppe „${choice.group}“ nicht übernommen: ${addErr.message}`);
    const groupAdded = added === true;
    if (groupAdded) {
      opts.onStatus?.(`Kategoriegruppe „${choice.group}“ wird in VARIO übernommen …`);
      const w = await syncCategoryTree(opts.supabase);
      if (w) return fail(w);
    }

    const assignOnce = async () => {
      const res = await fetch(`${opts.supabaseUrl.replace(/\/$/, '')}/functions/v1/otto-assign-listing-category`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${opts.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ articleId: provision.articleId, listingId: provision.listingId, group: choice.group, leaf: choice.leaf }),
      });
      const payload = (await res.json().catch(() => ({}))) as { varioCategoryId?: string; error?: { code?: string; message?: string } };
      return { ok: res.ok && !payload.error, status: res.status, payload };
    };
    let r = await assignOnce();
    const code = r.payload.error?.code;
    if (!r.ok && !groupAdded && (code === 'CATEGORY_NOT_IN_VARIO' || code === 'TREE_NOT_PUSHED')) {
      opts.onStatus?.(`Kategoriegruppe „${choice.group}“ wird in VARIO übernommen …`);
      const w = await syncCategoryTree(opts.supabase);
      if (w) return fail(w);
      r = await assignOnce();
    }
    if (!r.ok) return fail(`Kategorie nicht übernommen: ${r.payload.error?.message ?? httpErrorText(r.status)}`);

    const { error } = await opts.supabase.rpc('otto_set_listing_category', {
      _id: listerRowId, _otto_category: choice.leaf, _category_name: choice.leaf, _vario_category_id: r.payload.varioCategoryId ?? null,
    });
    if (error) return fail(`Kategorie in VARIO gesetzt, Lister nicht aktualisiert: ${error.message}`);
    return { status: 'set', groupAdded };
  } catch (e) {
    return fail(`Kategorie nicht übernommen: ${(e as Error).message}`);
  }
}

/** Ergebnis von completeOfferAssignment. `warn`: Kanal-Anlage gescheitert oder unvollständig.
 *  `notes`: was zusätzlich übernommen wurde (für die Erfolgsmeldung). `errors`: Kategorie/Marke
 *  konnten nicht übernommen werden (die Zuordnung selbst steht trotzdem). */
export interface CompleteAssignmentResult {
  warn: string | null;
  notes: string[];
  errors: string[];
}

/**
 * Was nach otto_assign_article passiert, für Einzel- und Sammel-Zuordnung gleich:
 * 1. OTTO-Angebotspreis in den Standard-Preis-Verkaufskanal (best-effort),
 * 2. OTTO-Verkaufskanal am VARIO-Artikel samt Lister-Zeile (otto-order-provision-channel),
 * 3. Kategorie und Marke übernehmen, falls entschieden.
 * `provision: false` (Bestellansicht) setzt nur den Preis, den Rest macht der Aufrufer.
 */
export async function completeOfferAssignment(
  opts: CategoryOpts & { provision?: boolean },
  unit: { sku: string; ean: string | null; priceCents?: number | null },
  decisions: { category: OfferCategoryChoice | null; brand: OfferBrandChoice | null },
): Promise<CompleteAssignmentResult> {
  const base = opts.supabaseUrl.replace(/\/$/, '');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${opts.token}` };
  const out: CompleteAssignmentResult = { warn: null, notes: [], errors: [] };

  const priceEur = typeof unit.priceCents === 'number' ? unit.priceCents / 100 : null;
  if (priceEur && priceEur > 0) {
    try {
      await fetch(`${base}/functions/v1/otto-set-channel-price`, {
        method: 'POST', headers, body: JSON.stringify({ sku: unit.sku, priceEur, source: 'assign' }),
      });
    } catch { /* best-effort */ }
  }
  if (opts.provision === false) return out;

  let result: ProvisionResult | null = null;
  try {
    const res = await fetch(`${base}/functions/v1/otto-order-provision-channel`, {
      method: 'POST', headers, body: JSON.stringify({ sku: unit.sku, ean: unit.ean ?? undefined, source: 'offer' }),
    });
    const j = (await res.json().catch(() => ({}))) as ProvisionResult;
    if (!res.ok || j.error) {
      out.warn = `Verkaufskanal nicht angelegt: ${j.error?.message ?? httpErrorText(res.status)}`;
      return out;
    }
    if (j.eanWritten && j.eanWritten.ok === false) {
      out.warn = `Verkaufskanal angelegt, EAN nicht gesetzt: ${j.eanWritten.message ?? ''}`;
      return out;
    }
    result = j;
  } catch (e) {
    out.warn = `Verkaufskanal nicht angelegt: ${(e as Error).message}`;
    return out;
  }

  const { category, brand } = decisions;
  if (category) {
    const c = await applyOfferCategory(opts, result, category);
    if (c.status === 'set') {
      out.notes.push(c.groupAdded
        ? `Kategorie „${category.leaf}“ samt Kategoriegruppe „${category.group}“ übernommen`
        : `Kategorie „${category.leaf}“ übernommen`);
    } else if (c.status === 'error') out.errors.push(c.message);
  }
  if (brand) {
    const b = await applyOfferBrand(opts.supabase, result, brand);
    if (b.status === 'set') {
      out.notes.push(b.brandAdded
        ? `Marke „${brand.name}“ übernommen und zu den präferierten Marken hinzugefügt`
        : `Marke „${brand.name}“ übernommen`);
    } else if (b.status === 'error') out.errors.push(b.message);
  }
  return out;
}

/** Einstellungen take/skip/ask für Kategorie und Marke (otto_get_assign_*_mode). */
export async function loadAssignModes(supabase: SupabaseClient): Promise<{ category: CategoryMode; brand: CategoryMode }> {
  const norm = (v: unknown): CategoryMode => (v === 'take' || v === 'ask' ? v : 'skip');
  const [c, b] = await Promise.all([
    supabase.rpc('otto_get_assign_category_mode', { _storefront: 'otto' }),
    supabase.rpc('otto_get_assign_brand_mode', { _storefront: 'otto' }),
  ]);
  return { category: norm(c.data), brand: norm(b.data) };
}

/** OTTO-Kategorie (mit eindeutiger Gruppe) und verwendbare OTTO-Marke eines Angebots, sonst null. */
export async function loadOfferChoices(
  supabase: SupabaseClient,
  sku: string,
): Promise<{ category: OfferCategoryChoice | null; brand: OfferBrandChoice | null }> {
  const [catRes, brandRes] = await Promise.all([
    supabase.rpc('otto_get_offer_category', { _sku: sku }),
    supabase.rpc('otto_get_offer_brand', { _sku: sku }),
  ]);
  const c = (Array.isArray(catRes.data) ? catRes.data[0] : catRes.data) as { category?: string | null; category_group?: string | null } | null;
  const b = (Array.isArray(brandRes.data) ? brandRes.data[0] : brandRes.data) as { brand_id?: string | null; brand_name?: string | null; usable?: boolean } | null;
  return {
    category: c?.category && c.category_group ? { group: c.category_group, leaf: c.category } : null,
    brand: b?.brand_id && b.brand_name && b.usable ? { id: b.brand_id, name: b.brand_name } : null,
  };
}

interface VarioArticleMatch {
  id: string;
  number: string | null;
  description: string | null;
  ean: string | null;
}

const SEARCH_DEBOUNCE_MS = 300;

export function openOttoAssignDialog(ctx: AssignDialogCtx): void {
  const isMapped = !!ctx.unit.varioArticleId;
  const mappedNumber = ctx.unit.varioArticleNumber ?? ctx.unit.varioArticleId ?? '—';
  const titleLabel = ctx.unit.title ?? '(ohne Titel)';
  const hasSuggestion = !isMapped && !!ctx.unit.suggestedVarioArticleId;

  // SKU/EAN sind im Suchmodus (nicht zugeordnet) anklickbar → befüllen das Suchfeld und suchen sofort.
  // Szenario (User): EAN vorbelegt, kein Treffer → SKU anklicken, dann wird nach der SKU gesucht.
  const searchable = !isMapped;
  const mag = '<span class="v-icon fa-light fa-magnifying-glass otto-fill-ic"></span>';
  const skuChip = searchable
    ? `<button type="button" class="otto-fill-search is-strong" data-fill="${escapeHtml(ctx.unit.sku)}" title="Nach dieser SKU suchen">SKU ${escapeHtml(ctx.unit.sku)} ${mag}</button>`
    : `<strong>SKU ${escapeHtml(ctx.unit.sku)}</strong>`;
  const eanChip = ctx.unit.ean
    ? (searchable
        ? ` · <button type="button" class="otto-fill-search" data-fill="${escapeHtml(ctx.unit.ean)}" title="Nach dieser EAN suchen">EAN ${escapeHtml(ctx.unit.ean)} ${mag}</button>`
        : ` · EAN ${escapeHtml(ctx.unit.ean)}`)
    : '';
  const meta = `
    <div class="kl-modal-meta">
      <div>${skuChip}</div>
      <div class="text-grey">${escapeHtml(titleLabel)}${eanChip}</div>
    </div>`;

  const suggestionHtml = hasSuggestion
    ? `<div class="kl-mapped-card">
         <div class="kl-mapped-card-icon"><span class="v-icon fa-light fa-wand-magic-sparkles"></span></div>
         <div class="kl-mapped-card-body">
           <div class="kl-mapped-card-title">Vorschlag - gleiche SKU/EAN in Vario</div>
           <div class="kl-mapped-card-num text-mono">${escapeHtml(ctx.unit.suggestedVarioArticleNumber ?? ctx.unit.suggestedVarioArticleId ?? '')}</div>
         </div>
         <button type="button" class="v-button v-button--primary" data-action="assign-suggested">
           <span class="v-icon fa-light fa-link"></span> Diesen zuordnen
         </button>
       </div>`
    : '';

  const body = isMapped
    ? `
      ${meta}
      <div class="kl-mapped-card">
        <div class="kl-mapped-card-icon"><span class="v-icon fa-light fa-link"></span></div>
        <div class="kl-mapped-card-body">
          <div class="kl-mapped-card-title">Aktuell zugeordnet</div>
          <div class="kl-mapped-card-num text-mono">${escapeHtml(mappedNumber)}</div>
        </div>
        <button type="button" class="v-button kl-mapped-unassign" data-action="unassign">
          <span class="v-icon fa-light fa-xmark"></span> Zuordnung aufheben
        </button>
      </div>
      <div class="kl-modal-error" id="otto-art-error" hidden></div>`
    : `
      ${meta}
      <div id="otto-art-category"></div>
      <div id="otto-art-brand"></div>
      ${suggestionHtml}
      <div class="kl-form-row">
        <label>Vario-Artikel suchen</label>
        <div class="v-input kl-search-input">
          <span class="v-icon fa-light fa-magnifying-glass"></span>
          <input id="otto-art-search" type="search" placeholder="EAN, SKU oder Bezeichnung …" autocomplete="off" />
          <button type="button" id="otto-art-search-clear" class="kl-search-clear" aria-label="Suche zurücksetzen" hidden>
            <span class="v-icon fa-light fa-xmark"></span>
          </button>
        </div>
        <div class="kl-form-hint">Beliebiger bestehender Vario-Artikel. Mindestens 3 Zeichen. Tipp: SKU oder EAN oben anklicken, um danach zu suchen.</div>
      </div>
      <div class="kl-search-results" id="otto-art-results"></div>
      <div class="kl-modal-error" id="otto-art-error" hidden></div>`;

  const modal = openModal({
    title: isMapped ? 'Vario-Artikel-Zuordnung' : 'Vario-Artikel zuordnen',
    icon: 'fa-link',
    width: 640,
    body,
    footer: `<button type="button" class="v-button" data-modal-cancel>Schließen</button>`,
  });

  const errorEl = modal.el.querySelector<HTMLDivElement>('#otto-art-error')!;
  const showError = (msg: string) => { errorEl.textContent = msg; errorEl.hidden = false; };
  const clearError = () => { errorEl.textContent = ''; errorEl.hidden = true; };

  // Kategorie-Übernahme: Einstellung + Kategorie des Angebots laden. Bei „fragen“ eine Checkbox
  // (vorbelegt), bei „übernehmen“ nur ein Hinweis. Ohne bekannte Kategorie/Gruppe wird nichts übernommen.
  let categoryMode: CategoryMode = 'skip';
  let offerCategory: OfferCategoryChoice | null = null;
  // Zuordnen wartet darauf, sonst ginge bei einem schnellen Klick die Entscheidung verloren.
  const categoryInfoReady: Promise<void> = isMapped ? Promise.resolve() : loadCategoryInfo();

  async function loadCategoryInfo(): Promise<void> {
    const host = modal.el.querySelector<HTMLDivElement>('#otto-art-category');
    if (!host) return;
    try {
      const [modeRes, catRes] = await Promise.all([
        ctx.supabase.rpc('otto_get_assign_category_mode', { _storefront: 'otto' }),
        ctx.supabase.rpc('otto_get_offer_category', { _sku: ctx.unit.sku }),
      ]);
      const mode = modeRes.data;
      categoryMode = mode === 'take' || mode === 'ask' ? mode : 'skip';
      const row = (Array.isArray(catRes.data) ? catRes.data[0] : catRes.data) as
        { category?: string | null; category_group?: string | null; group_count?: number } | null;
      const leaf = row?.category ?? null;
      const group = row?.category_group ?? null;
      if (leaf && group) offerCategory = { group, leaf };
      if (categoryMode === 'skip') return;
      if (!leaf) {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Kategorie des Angebots noch nicht abgerufen, sie wird nicht übernommen.</div>`;
      } else if (!group) {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Kategorie „${escapeHtml(leaf)}“ liegt in mehreren Kategoriegruppen. Bitte nach der Zuordnung im Lister wählen.</div>`;
      } else if (categoryMode === 'ask') {
        host.innerHTML = `<label class="kl-check-row" style="display:flex;align-items:center;gap:10px;padding:6px 2px;cursor:pointer;">
            <input type="checkbox" id="otto-art-take-category" checked>
            <span style="flex:1;">OTTO-Kategorie „${escapeHtml(leaf)}“ übernehmen, falls der VARIO-Artikel noch keine hat</span>
          </label>`;
      } else {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Kategorie „${escapeHtml(leaf)}“ wird übernommen, falls der VARIO-Artikel noch keine hat.</div>`;
      }
    } catch { /* best-effort: ohne Info wird keine Kategorie übernommen */ }
  }

  // Marken-Übernahme: gleiches Muster wie die Kategorie (Einstellung + Marke des Angebots).
  let brandMode: CategoryMode = 'skip';
  let offerBrand: OfferBrandChoice | null = null;
  const brandInfoReady: Promise<void> = isMapped ? Promise.resolve() : loadBrandInfo();

  async function loadBrandInfo(): Promise<void> {
    const host = modal.el.querySelector<HTMLDivElement>('#otto-art-brand');
    if (!host) return;
    try {
      const [modeRes, brandRes] = await Promise.all([
        ctx.supabase.rpc('otto_get_assign_brand_mode', { _storefront: 'otto' }),
        ctx.supabase.rpc('otto_get_offer_brand', { _sku: ctx.unit.sku }),
      ]);
      const mode = modeRes.data;
      brandMode = mode === 'take' || mode === 'ask' ? mode : 'skip';
      const row = (Array.isArray(brandRes.data) ? brandRes.data[0] : brandRes.data) as
        { brand_id?: string | null; brand_name?: string | null; usable?: boolean } | null;
      const id = row?.brand_id ?? null;
      const name = row?.brand_name ?? null;
      if (id && name && row?.usable) offerBrand = { id, name };
      if (brandMode === 'skip') return;
      if (!id) {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Marke des Angebots noch nicht abgerufen, sie wird nicht übernommen.</div>`;
      } else if (!offerBrand) {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Marke ${escapeHtml(name ?? id)} ist im Markenkatalog nicht verwendbar und wird nicht übernommen. Bitte nach der Zuordnung im Lister wählen.</div>`;
      } else if (brandMode === 'ask') {
        host.innerHTML = `<label class="kl-check-row" style="display:flex;align-items:center;gap:10px;padding:6px 2px;cursor:pointer;">
            <input type="checkbox" id="otto-art-take-brand" checked>
            <span style="flex:1;">OTTO-Marke „${escapeHtml(offerBrand.name)}“ übernehmen, falls der Artikel im Lister noch keine hat</span>
          </label>`;
      } else {
        host.innerHTML = `<div class="kl-form-hint">OTTO-Marke „${escapeHtml(offerBrand.name)}“ wird übernommen, falls der Artikel im Lister noch keine hat.</div>`;
      }
    } catch { /* best-effort: ohne Info wird keine Marke übernommen */ }
  }

  /** Entscheidung bei der Zuordnung: Marke übernehmen (welche) oder null. */
  function brandDecision(): OfferBrandChoice | null {
    if (!offerBrand) return null;
    if (brandMode === 'take') return offerBrand;
    if (brandMode === 'ask') {
      return modal.el.querySelector<HTMLInputElement>('#otto-art-take-brand')?.checked ? offerBrand : null;
    }
    return null;
  }

  /** Entscheidung bei der Zuordnung: Kategorie übernehmen (mit welcher) oder null. */
  function categoryDecision(): OfferCategoryChoice | null {
    if (!offerCategory) return null;
    if (categoryMode === 'take') return offerCategory;
    if (categoryMode === 'ask') {
      return modal.el.querySelector<HTMLInputElement>('#otto-art-take-category')?.checked ? offerCategory : null;
    }
    return null;
  }

  if (isMapped) {
    wireUnassign(modal.el.querySelector<HTMLButtonElement>('[data-action="unassign"]')!);
  } else {
    if (hasSuggestion) {
      const sBtn = modal.el.querySelector<HTMLButtonElement>('[data-action="assign-suggested"]')!;
      sBtn.addEventListener('click', () =>
        void assign(ctx.unit.suggestedVarioArticleId!, ctx.unit.suggestedVarioArticleNumber ?? null, null, sBtn));
    }
    wireSearch();
  }

  function wireSearch(): void {
    const searchInput = modal.el.querySelector<HTMLInputElement>('#otto-art-search')!;
    const clearBtn = modal.el.querySelector<HTMLButtonElement>('#otto-art-search-clear')!;
    const resultsEl = modal.el.querySelector<HTMLDivElement>('#otto-art-results')!;
    const updateClear = () => { clearBtn.hidden = searchInput.value.length === 0; };

    let debounce: number | null = null;
    searchInput.addEventListener('input', () => {
      updateClear();
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => void runSearch(searchInput.value, resultsEl), SEARCH_DEBOUNCE_MS) as unknown as number;
    });
    clearBtn.addEventListener('click', () => {
      searchInput.value = '';
      updateClear();
      resultsEl.innerHTML = renderEmptyHint();
      searchInput.focus();
    });

    // SKU/EAN-Chips im Meta-Block: Klick befüllt das Suchfeld und sucht sofort (ohne Debounce).
    modal.el.querySelectorAll<HTMLButtonElement>('.otto-fill-search').forEach((chip) => {
      chip.addEventListener('click', () => {
        const val = chip.dataset['fill'] ?? '';
        if (!val) return;
        if (debounce !== null) window.clearTimeout(debounce);
        searchInput.value = val;
        updateClear();
        searchInput.focus();
        void runSearch(val, resultsEl);
      });
    });

    // Mit EAN vorbelegen, falls vorhanden - sonst SKU.
    const prefill = ctx.unit.ean ?? ctx.unit.sku;
    if (prefill) {
      searchInput.value = prefill;
      updateClear();
      void runSearch(prefill, resultsEl);
    } else {
      resultsEl.innerHTML = renderEmptyHint();
    }
    setTimeout(() => { searchInput.focus(); searchInput.select(); }, 50);
  }

  async function runSearch(term: string, resultsEl: HTMLDivElement): Promise<void> {
    const trimmed = term.trim();
    if (trimmed.length === 0) { resultsEl.innerHTML = renderEmptyHint(); return; }
    if (trimmed.length < 3) {
      resultsEl.innerHTML = `<div class="kl-search-empty"><span class="v-icon fa-light fa-circle-info"></span> Mindestens 3 Zeichen eingeben.</div>`;
      return;
    }
    resultsEl.innerHTML = `<div class="kl-search-loading"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Suche in Vario …</div>`;
    clearError();
    try {
      const res = await fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-search-articles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ search: trimmed, limit: 20, marketplace: 'otto' }),
      });
      const payload = (await res.json()) as { articles?: VarioArticleMatch[]; error?: { message: string } };
      if (!res.ok || !payload.articles) throw new Error(payload.error?.message ?? httpErrorText(res.status));
      renderResults(payload.articles, resultsEl);
    } catch (e) {
      showError(`Suche fehlgeschlagen: ${(e as Error).message}`);
      resultsEl.innerHTML = '';
    }
  }

  function renderResults(articles: VarioArticleMatch[], resultsEl: HTMLDivElement): void {
    if (articles.length === 0) {
      resultsEl.innerHTML = `<div class="v-banner v-banner-info"><span class="v-icon fa-light fa-circle-info"></span><div><strong>Keine Treffer.</strong> Suchbegriff anpassen (EAN, SKU oder Bezeichnung).</div></div>`;
      return;
    }
    resultsEl.innerHTML = articles.map((a) => `
      <button type="button" class="kl-search-result" data-action="assign"
        data-id="${escapeHtml(a.id)}" data-number="${escapeHtml(a.number ?? '')}" data-name="${escapeHtml(a.description ?? '')}">
        <div class="kl-search-result-main">
          <div class="kl-search-result-title">${escapeHtml(a.description ?? '(ohne Bezeichnung)')}</div>
          <div class="kl-search-result-meta">
            <span class="text-mono">${escapeHtml(a.number ?? '—')}</span>
            <span class="text-grey">· EAN <span class="text-mono">${escapeHtml(a.ean ?? '—')}</span></span>
          </div>
        </div>
        <span class="v-icon fa-light fa-arrow-right kl-search-result-arrow"></span>
      </button>`).join('');
    resultsEl.querySelectorAll<HTMLButtonElement>('button[data-action="assign"]').forEach((btn) => {
      btn.addEventListener('click', () =>
        void assign(btn.dataset['id']!, btn.dataset['number'] || null, btn.dataset['name'] || null, btn));
    });
  }

  function renderEmptyHint(): string {
    return `<div class="kl-search-empty"><span class="v-icon fa-light fa-magnifying-glass"></span> Suchbegriff eingeben - EAN, SKU oder Bezeichnung.</div>`;
  }

  async function assign(
    varioArticleId: string,
    varioArticleNumber: string | null,
    varioArticleName: string | null,
    btn: HTMLButtonElement,
  ): Promise<void> {
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-loading');
    clearError();
    try {
      // RPC ordnet zu UND enqueued einen gezielten Vario-Bestand-Refresh → gibt dessen Job-uuid zurück.
      const { data, error } = await ctx.supabase.rpc('otto_assign_article', {
        _sku: ctx.unit.sku,
        _vario_article_id: varioArticleId,
        _vario_article_number: varioArticleNumber,
        _vario_article_name: varioArticleName,
      });
      if (error) throw new Error(error.message);
      // Kurz auf den Refresh warten, damit der aktuelle Vario-Bestand beim Reload sofort dasteht.
      // Best-effort: bei Timeout/Fehler wird trotzdem geschlossen (der Bestand füllt sonst der Cron).
      const jobId = typeof data === 'string' ? data : Array.isArray(data) ? (data[0] as string) : null;
      showToast('Zugeordnet - Vario-Bestand wird geladen…', 'info');
      if (jobId) {
        try { await awaitJob(ctx.supabase, jobId, { timeoutMs: 30_000, pollIntervalMs: 1500 }); } catch { /* best-effort */ }
      }
      // Danach Kanalpreis, OTTO-Verkaufskanal am Vario-Artikel samt Lister-Zeile, Kategorie und
      // Marke (completeOfferAssignment, gleicher Ablauf wie die Sammel-Zuordnung). Scheitert die
      // Kanal-Anlage, bleibt die Zuordnung bestehen → Hinweis statt Fehler.
      await Promise.all([categoryInfoReady, brandInfoReady]);
      const category = categoryDecision();
      const brand = brandDecision();
      const done = await completeOfferAssignment({
        supabase: ctx.supabase, supabaseUrl: ctx.supabaseUrl, token: ctx.session.token,
        onStatus: (msg) => showToast(msg, 'info'),
        provision: ctx.provisionChannel !== false,
      }, ctx.unit, { category, brand });
      for (const err of done.errors) showToast(err, 'error');
      if (ctx.provisionChannel === false) showToast('Angebot zugeordnet.', 'success');
      else if (done.warn) showToast(`Angebot zugeordnet. ${done.warn}`, 'error');
      else showToast(`Angebot zugeordnet, Verkaufskanal am Artikel angelegt${done.notes.map((n) => `, ${n}`).join('')}.`, 'success');
      ctx.onDone({ category, brand });
      modal.close();
    } catch (e) {
      showError(`Zuweisen fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.innerHTML = originalHtml;
    }
  }

  // Aufheben: zweistufiger Klick (kein window.confirm). Erster Klick „schärft", zweiter führt aus;
  // nach 4 s ohne zweiten Klick zurücksetzen.
  function wireUnassign(btn: HTMLButtonElement): void {
    const originalHtml = btn.innerHTML;
    let armed = false;
    let armTimer: number | null = null;
    btn.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        btn.classList.add('is-armed');
        btn.innerHTML = '<span class="v-icon fa-light fa-triangle-exclamation"></span> Wirklich aufheben? Nochmal klicken';
        if (armTimer !== null) window.clearTimeout(armTimer);
        armTimer = window.setTimeout(() => {
          armed = false;
          btn.classList.remove('is-armed');
          btn.innerHTML = originalHtml;
        }, 4000) as unknown as number;
        return;
      }
      if (armTimer !== null) window.clearTimeout(armTimer);
      void unassign(btn, originalHtml);
    });
  }

  async function unassign(btn: HTMLButtonElement, originalHtml: string): Promise<void> {
    btn.disabled = true;
    btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Aufheben …';
    clearError();
    try {
      const { error } = await ctx.supabase.rpc('otto_unassign_article', { _sku: ctx.unit.sku });
      if (error) throw new Error(error.message);
      showToast('Zuordnung aufgehoben.', 'success');
      ctx.onDone();
      modal.close();
    } catch (e) {
      showError(`Aufheben fehlgeschlagen: ${(e as Error).message}`);
      btn.disabled = false;
      btn.classList.remove('is-armed');
      btn.innerHTML = originalHtml;
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;');
}
